import {AssetType} from 'common/src/modules/Assets/Assets.enums';
import getAllAssetsFromTree from 'common/src/modules/Assets/utils/getAllAssetsFromTree';
import getAssetUrlLink from 'common/src/modules/Assets/utils/getAssetUrlLink';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import appendLayers from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/actions/appendLayers';
import createTextLayer from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/utils/createTextLayer';
import {TemplateAssetType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {TemplateAsset} from 'common/src/modules/dynamicParameters/dynamicParameters.types';
import {
  HorizontalAlignment,
  MovieLayerType,
  MovieResizeOption,
  VerticalAlignment,
} from 'common/src/modules/movie/movie.enums';
import {
  MovieImageLayer,
  MovieShapeLayer,
  MovieVideoLayer,
} from 'common/src/modules/movie/movieLayers.types';
import simpleMovieValue from 'common/src/modules/movie/utils/simpleMovieValue';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import getFileNameFromUrl from 'common/src/utils/urlUtils/getFileNameFromUrl';
import {z} from 'zod';
import getShapeContentFromPrompt from '../../utils/getShapeContentFromPrompt';
import getTextContentFromPrompt from '../../utils/getTextContentFromPrompt';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Simple schema for natural language input
const inputSchema = z.object({
  layerType: z.enum(['text', 'image', 'video', 'shape']).describe('Type of layer to create'),
  x: z.number().describe('Layer transform X position in pixels'),
  y: z.number().describe('Layer transform Y position in pixels'),
  width: z
    .number()
    .nullable()
    .optional()
    .describe('Layer width in pixels (do not provide it for shape layers)'),
  height: z
    .number()
    .nullable()
    .optional()
    .describe('Layer height in pixels (do not provide it for shape layers)'),
  name: z.string().nullable().optional().describe('Custom layer name'),
  startTime: z
    .number()
    .nullable()
    .optional()
    .describe('Start time in milliseconds (defaults to 0)'),
  endTime: z
    .number()
    .nullable()
    .optional()
    .describe('End time in milliseconds (defaults to composition duration)'),
  contentDescription: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Natural language description of the layer content (for shape/text layers only) including fill/stroke/opacity information. Important: advise if the shape geometry is relative to layer transform position or composition (composition top/left is 0,0)',
    ),

  // Asset IDs for specific layer types
  fontId: z.string().nullable().optional().describe('Font asset ID (required for text layers)'),
  imageAssetId: z
    .number()
    .nullable()
    .optional()
    .describe('Numeric image asset ID (required for image layers)'),
  videoAssetId: z
    .number()
    .nullable()
    .optional()
    .describe('Numeric video asset ID (required for video layers)'),
});

function addLayer2Tool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.AddLayer2,
    displayName: 'Adding layer',
    description:
      'Adds a new layer (text, image, video, or shape) to the active composition using natural language description. If transform rotation/opacity is required, it should be done on a separate call to other respective tool',
    inputSchema,
    execute: async ({
      input: params,
      toolUtils,
      callChain,
    }: ToolExecuteParams<typeof inputSchema>) => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return toolError('No active composition found');
      }

      try {
        let layerId: string;
        let unsupportedRequest: string | undefined;

        switch (params.layerType) {
          case 'text': {
            // Create text layer with generated content
            const templateAssets =
              store.getState().creativeTemplatePage.templateFonts.templateAssets;
            const fontAssets = templateAssets.filter(
              (asset) => asset.metadata.type === TemplateAssetType.Font,
            );

            let fontMetadata: TemplateAsset['metadata'];

            if (params.fontId) {
              const selectedFont = templateAssets.find(
                (asset) =>
                  asset.id === params.fontId && asset.metadata.type === TemplateAssetType.Font,
              );

              if (!selectedFont) {
                throw new Error(`Font with ID "${params.fontId}" not found`);
              }

              fontMetadata = selectedFont.metadata;
            } else if (fontAssets.length > 0) {
              fontMetadata = fontAssets[0].metadata;
            } else {
              throw new Error(
                `No Fonts available in Template. please add at least one font and try again`,
              );
            }

            const textLayer = createTextLayer(fontMetadata);

            if (params.name) textLayer.name = params.name;

            textLayer.tr.p = simpleMovieValue([params.x, params.y, 0]);

            if (params.startTime !== null && params.startTime !== undefined) {
              textLayer.s = params.startTime;
            }

            if (params.endTime !== null && params.endTime !== undefined) {
              textLayer.e = params.endTime;
            } else {
              textLayer.e = activeComp.dur * 1000;
            }

            // Use utility function to generate text content
            const textResult = await getTextContentFromPrompt({
              contentDescription: params.contentDescription || 'Default text content',
              width: params.width || null,
              height: params.height || null,
              fontMetadata,
              existingAeSrcTxt: undefined,
              toolUtils,
              callChain,
            });

            // Update the text layer with the generated content
            textLayer.text.aeSrcTxt = textResult.aeSrcTxt;
            unsupportedRequest = textResult.unsupportedRequest;

            const newLayerIds = await store.dispatch(appendLayers({layers: [textLayer]})).unwrap();
            layerId = newLayerIds[0];
            break;
          }

          case 'image': {
            if (!params.imageAssetId) {
              throw new Error('Image layers require imageAssetId parameter');
            }

            if (!params.width || !params.height) {
              throw new Error('Image layers require width and height parameters');
            }

            const assetTree = store.getState().creativeTemplatePage.templateAssets.assetTree;

            if (!assetTree) {
              throw new Error('Template assets not loaded');
            }

            const allAssets = getAllAssetsFromTree(assetTree);
            const selectedImageAsset = allAssets.find(
              (asset) => asset.id === params.imageAssetId && asset.assetType === AssetType.Image,
            );

            if (!selectedImageAsset) {
              throw new Error(`Image asset with ID "${params.imageAssetId}" not found`);
            }

            const imageUrl = getAssetUrlLink(selectedImageAsset);

            if (!imageUrl) {
              throw new Error(`Image asset "${params.imageAssetId}" does not have a valid URL`);
            }

            const imageLayer: MovieImageLayer = {
              ty: MovieLayerType.Image,
              name: params.name || selectedImageAsset.name || getFileNameFromUrl(imageUrl) || '',
              mappingId: Math.floor(Math.random() * 1e8),
              en: true,
              aEn: false,
              mb: false,
              s: params.startTime ?? 0,
              e: params.endTime ?? activeComp.dur * 1000,
              is3d: false,
              tmt: 0,
              w: params.width,
              h: params.height,
              i: '1',
              tr: {
                p: simpleMovieValue([params.x, params.y, 0]),
                an: simpleMovieValue([params.width / 2, params.height / 2, 0]),
                s: simpleMovieValue([1, 1, 1]),
                rx: simpleMovieValue([0]),
                ry: simpleMovieValue([0]),
                rz: simpleMovieValue([0]),
                a: simpleMovieValue([1]),
              },
              eff: [],
              masks: [],
              image: {
                w: params.width,
                h: params.height,
                naturalHeight: params.height,
                naturalWidth: params.width,
                x: 0,
                y: 0,
                url: imageUrl.replace(/^https?:/, ''),
                ro: MovieResizeOption.Crop,
                vAlign: VerticalAlignment.Center,
                hAlign: HorizontalAlignment.Center,
              },
            };

            const newLayerIds = await store.dispatch(appendLayers({layers: [imageLayer]})).unwrap();
            layerId = newLayerIds[0];
            break;
          }

          case 'video': {
            if (!params.videoAssetId) {
              throw new Error('Video layers require videoAssetId parameter');
            }

            if (!params.width || !params.height) {
              throw new Error('Video layers require width and height parameters');
            }

            const assetTree = store.getState().creativeTemplatePage.templateAssets.assetTree;

            if (!assetTree) {
              throw new Error('Template assets not loaded');
            }

            const allAssets = getAllAssetsFromTree(assetTree);
            const selectedVideoAsset = allAssets.find(
              (asset) => asset.id === params.videoAssetId && asset.assetType === AssetType.Video,
            );

            if (!selectedVideoAsset) {
              throw new Error(`Video asset with ID "${params.videoAssetId}" not found`);
            }

            const videoUrl = getAssetUrlLink(selectedVideoAsset);

            if (!videoUrl) {
              throw new Error(`Video asset "${params.videoAssetId}" does not have a valid URL`);
            }

            const videoLayer: MovieVideoLayer = {
              ty: MovieLayerType.Video,
              name: params.name || selectedVideoAsset.name || getFileNameFromUrl(videoUrl) || '',
              mappingId: Math.floor(Math.random() * 1e8),
              en: true,
              aEn: true,
              mb: false,
              s: params.startTime ?? 0,
              e: params.endTime ?? activeComp.dur * 1000,
              is3d: false,
              tmt: 0,
              w: params.width,
              h: params.height,
              i: '1',
              tr: {
                p: simpleMovieValue([params.x, params.y, 0]),
                an: simpleMovieValue([params.width / 2, params.height / 2, 0]),
                s: simpleMovieValue([1, 1, 1]),
                rx: simpleMovieValue([0]),
                ry: simpleMovieValue([0]),
                rz: simpleMovieValue([0]),
                a: simpleMovieValue([1]),
              },
              eff: [],
              masks: [],
              video: {
                w: params.width,
                h: params.height,
                x: 0,
                y: 0,
                al: simpleMovieValue([0, 0]),
                url: videoUrl.replace(/^https?:/, ''),
                ls: 0,
                loop: 1,
                muted: 1,
              },
            };

            const newLayerIds = await store.dispatch(appendLayers({layers: [videoLayer]})).unwrap();
            layerId = newLayerIds[0];
            break;
          }

          case 'shape': {
            // Use utility function to generate shape content
            const {
              shape,
              shapeType,
              unsupportedRequest: shapeUnsupportedRequest,
            } = await getShapeContentFromPrompt({
              shapeDescription: `The shape is inside a layer with transform position of {${params.x},${params.y}}, here is its description: ${params.contentDescription}`,
              toolUtils,
              callChain,
            });

            unsupportedRequest = shapeUnsupportedRequest;

            const shapeLayer: MovieShapeLayer = {
              ty: MovieLayerType.Shape,
              name: params.name || `New ${shapeType}`,
              mappingId: Math.floor(Math.random() * 1e8),
              en: true,
              aEn: false,
              mb: false,
              s: params.startTime ?? 0,
              e: params.endTime ?? activeComp.dur * 1000,
              is3d: false,
              tmt: 0,
              w: 0,
              h: 0,
              i: '1',
              tr: {
                p: simpleMovieValue([params.x, params.y, 0]),
                an: simpleMovieValue([0, 0, 0]),
                s: simpleMovieValue([1, 1, 1]),
                rx: simpleMovieValue([0]),
                ry: simpleMovieValue([0]),
                rz: simpleMovieValue([0]),
                a: simpleMovieValue([1]),
              },
              eff: [],
              masks: [],
              shape: {
                shapes: [shape],
              },
            };

            const newLayerIds = await store.dispatch(appendLayers({layers: [shapeLayer]})).unwrap();
            layerId = newLayerIds[0];
            break;
          }

          default:
            throw new Error(`Unsupported layer type: ${params.layerType}`);
        }

        return {
          content: `Added ${params.layerType} layer id: ${layerId}${unsupportedRequest ? ` (Note: ${unsupportedRequest})` : ''}`,
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const trimmedError =
          errorMessage.length > 100 ? errorMessage.substring(0, 100) + '...' : errorMessage;

        return {
          content: `Failed to add ${params.layerType} layer: ${trimmedError}`,
        };
      }
    },
  });
}

export default addLayer2Tool;
